import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { RetweetOutlined } from "@ant-design/icons";
import { Button, Popconfirm, Tag, Tooltip, theme } from "antd";
import { observer } from "mobx-react";
import { EAffiliateCouponType } from "@devowl-wp/api-real-commerce";
import { RealCommerceError } from "../../../../../../exceptions/realCommerceError.js";
import { useRealCommerceErrorHandling } from "../../../../../../hooks/useRealCommerceErrorHandling.js";
import { useStores } from "../../../../../../store/stores.js";
import { __ } from "../../../../../../utils/i18n.js";
import { EmptyState } from "../../../emptyState.js";
import { LicenseKeyPill } from "../../../licenseKeyPill.js";
import { AffiliatePanel } from "../affiliatePanel.js";
// Card-row list (no data grid) — mirrors the license-key row treatment: code
// pill, product tags, the discount + recurring marker, and a delete action.
const CouponsTable = observer(()=>{
    const { token } = theme.useToken();
    const handleRealCommerceError = useRealCommerceErrorHandling();
    const { accountV2Store: { session } } = useStores();
    const affiliate = session?.affiliate;
    if (!affiliate) {
        return null;
    }
    const { isBusyCoupons, coupons } = affiliate;
    const isEmpty = coupons.size === 0 && !isBusyCoupons;
    const handleDelete = async (record)=>{
        try {
            await record.delete();
        } catch (e) {
            if (e instanceof RealCommerceError) {
                handleRealCommerceError(e);
            }
        }
    };
    return /*#__PURE__*/ _jsx(AffiliatePanel, {
        heading: __("Your coupons"),
        children: isEmpty ? /*#__PURE__*/ _jsx(EmptyState, {
            heading: __("No coupons created yet"),
            subline: __("Create one to share with your audience.")
        }) : /*#__PURE__*/ _jsx("div", {
            className: "rc-account-v2-affiliate-coupon-list",
            children: Array.from(coupons.values()).map((coupon)=>{
                const { id, code, products, discountAmount, discountType, isRecurring } = coupon.data;
                return /*#__PURE__*/ _jsxs("div", {
                    className: "rc-account-v2-affiliate-coupon-row",
                    children: [
                        /*#__PURE__*/ _jsx(LicenseKeyPill, {
                            value: code,
                            copyLabel: __("Copy coupon code"),
                            copiedMessage: __("Coupon code copied to clipboard.")
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "rc-account-v2-affiliate-coupon-row__products",
                            children: products.map((product)=>/*#__PURE__*/ _jsx(Tag, {
                                    children: product.name
                                }, product.id))
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "rc-account-v2-affiliate-coupon-row__discount",
                            children: [
                                `${discountAmount} ${discountType === EAffiliateCouponType.Flat ? "EUR" : "%"}`,
                                isRecurring && /*#__PURE__*/ _jsx(Tooltip, {
                                    title: __("The discount applies to recurring payments too, not just the first invoicing period."),
                                    children: /*#__PURE__*/ _jsx(RetweetOutlined, {
                                        style: {
                                            color: token.colorPrimary
                                        }
                                    })
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Popconfirm, {
                            title: __("Delete this coupon?"),
                            okText: __("Delete"),
                            okButtonProps: {
                                danger: true,
                                loading: coupon.isBusy
                            },
                            onConfirm: ()=>handleDelete(coupon),
                            children: /*#__PURE__*/ _jsx(Button, {
                                danger: true,
                                type: "primary",
                                size: "small",
                                children: __("Delete")
                            })
                        })
                    ]
                }, id);
            })
        })
    });
});
export { CouponsTable };
